import type { Menu, Order, PricedModifier } from "../../api/types";
import { AppIcon } from "../AppIcon";
import { describeModifier, noteText } from "../../features/sale/modifiers";
import { shownVariant } from "../../features/sale/quick-entry";
import type { QuoteState } from "../../features/sale/useQuote";
import { t } from "../../i18n";
import { formatEuro, type Cents } from "../../lib/money";
import { formatTime } from "../../lib/time";
import type { ApiClient } from "../../api/client";
import type { DraftLine } from "../app-storage";
import { TakeawayCustomer, type TakeawayDetails } from "./TakeawayCustomer";
import { MAX_QUANTITY, type Spot } from "./waiter";

interface Props {
  spot: Spot;
  /** The open order of the table; null for a free table and for the takeaway. */
  order: Order | null;
  draft: readonly DraftLine[];
  api: ApiClient;
  /** The takeaway's customer and note (Afhaalgegevens). */
  details: TakeawayDetails;
  menu: Menu;
  /** The quote of what is new (the prices of the lines not sent yet). */
  priced: QuoteState;
  /** What the bill comes to (what is still due); null while the server prices it. */
  total: Cents | null;
  /** iPad: the bill on the left, the products on the right (prototype ipad2). */
  wide: boolean;
  busy: boolean;
  onBack: () => void;
  onMove: () => void;
  onDetails: (change: Partial<TakeawayDetails>) => void;
  onRemove: (key: string) => void;
  /** − or + on a line not sent yet. */
  onQuantity: (key: string, delta: number) => void;
  onPick: () => void;
  onSend: () => void;
  onBill: () => void;
  onPay: () => void;
  onLater: () => void;
}

/**
 * One line of the bill (prototype obLineRow): "1× Margherita Medium", the options small, the amount. A line
 * not sent yet shows its number between − and + instead (Selim, 18/10/2026: two colas in one go).
 */
export function LineRow({
  title,
  modifiers,
  note,
  amount,
  quantity,
  onRemove,
}: {
  title: string;
  modifiers: string[];
  note: string | null;
  amount: Cents | null;
  /** `max`: the most of one line (999, a guest at the table 50). */
  quantity?: { count: number; fixed: boolean; max?: number; onChange: (delta: number) => void };
  onRemove?: () => void;
}) {
  const small = [...modifiers, ...(note === null || note === "" ? [] : [noteText(note)])];
  return (
    <div className="ob-line">
      <div>
        <b>{title}</b>
        {small.length > 0 && <small>{small.join(" · ")}</small>}
      </div>
      {quantity && (
        <div className="qty ob-qty">
          <button
            type="button"
            aria-label={`${t.tafoApp.waiter.less} ${title}`}
            disabled={quantity.fixed || quantity.count <= 1}
            onClick={() => {
              quantity.onChange(-1);
            }}
          >
            −
          </button>
          <output aria-label={`${t.tafoApp.waiter.quantity} ${title}`}>{quantity.count}</output>
          <button
            type="button"
            aria-label={`${t.tafoApp.waiter.more} ${title}`}
            disabled={quantity.fixed || quantity.count >= (quantity.max ?? MAX_QUANTITY)}
            onClick={() => {
              quantity.onChange(1);
            }}
          >
            +
          </button>
        </div>
      )}
      <span className="num">{amount === null ? "…" : formatEuro(amount)}</span>
      {onRemove && (
        <button type="button" className="ob-x" aria-label={`${t.tafoApp.waiter.remove} ${title}`} onClick={onRemove}>
          ×
        </button>
      )}
    </div>
  );
}

export const modifierTexts = (modifiers: readonly PricedModifier[]) =>
  modifiers.map((modifier) => describeModifier(modifier));

/**
 * A table or the takeaway (prototype obOrderHtml): what is already in the kitchen, what is new and not
 * sent yet (× takes it off), the total, and Naar keuken, Rekening and Afrekenen; for the takeaway
 * "Betalen bij afhalen" or "Nu afrekenen".
 */
export function OrderView({
  spot,
  order,
  draft,
  api,
  details,
  menu,
  priced,
  total,
  wide,
  busy,
  onBack,
  onMove,
  onDetails,
  onRemove,
  onQuantity,
  onPick,
  onSend,
  onBill,
  onPay,
  onLater,
}: Props) {
  const text = t.tafoApp.waiter;
  const table = spot.kind === "table";
  const sent = order?.lines ?? [];
  const any = sent.length + draft.length > 0;

  /** The number stands between − and +, so the title is the product and its size. */
  const draftTitle = (line: DraftLine) => {
    const product = menu.products[line.productId];
    const variant = shownVariant(product, line);
    return `${product?.name ?? line.productId}${variant === null ? "" : ` ${variant}`}`;
  };

  return (
    <>
      {/* back to the tables; the takeaway is a tab of its own, "‹ terug" led nowhere (Furkan, 19/10/2026) */}
      {table && (
        <button type="button" className="linkbtn" onClick={onBack}>
          {wide ? text.allTables : text.backTables}
        </button>
      )}
      <div className="ob-thead">
        <h3>
          {table ? text.table(spot.table) : text.takeaway}
          {order && <small className="proto"> {text.since(formatTime(order.createdAt))}</small>}
        </h3>
        {table && any && (
          <button type="button" className="btn ob-mv" disabled={busy} onClick={onMove}>
            <AppIcon name="move" />
            {text.move}
          </button>
        )}
      </div>
      {!table && <TakeawayCustomer api={api} details={details} onChange={onDetails} />}
      {sent.length > 0 && (
        <>
          <div className="ph-sec">{text.sent}</div>
          {sent.map((line, index) => (
            <LineRow
              key={`sent-${String(index)}`}
              title={`${String(line.quantity)}× ${line.name}${line.variant === null ? "" : ` ${line.variant}`}`}
              modifiers={modifierTexts(line.modifiers)}
              note={line.note}
              amount={line.lineCents}
            />
          ))}
        </>
      )}
      {draft.length > 0 && (
        <>
          <div className="ph-sec">{text.draft}</div>
          {draft.map((line) => {
            const quoted = priced.priced(line);
            return (
              <LineRow
                key={line.key}
                title={draftTitle(line)}
                modifiers={quoted ? modifierTexts(quoted.modifiers) : []}
                note={line.note ?? null}
                amount={quoted ? quoted.lineCents : null}
                quantity={{
                  count: line.quantity,
                  fixed: line.promotion === true,
                  onChange: (delta) => {
                    onQuantity(line.key, delta);
                  },
                }}
                onRemove={() => {
                  onRemove(line.key);
                }}
              />
            );
          })}
        </>
      )}
      {!any && (
        <p className="proto ph-empty">
          {table ? text.tableFree : text.nothingYet}
          {wide && ` ${text.ipadHint}`}
        </p>
      )}
      {!wide &&
        (any ? (
          <button type="button" className="btn ph-big" disabled={busy} onClick={onPick}>
            {text.addProduct}
          </button>
        ) : (
          <button type="button" className="btn primary ph-big" disabled={busy} onClick={onPick}>
            {text.start}
          </button>
        ))}
      {any && (
        <div className="ob-tot">
          <span>{text.total}</span>
          <b className="num">{total === null ? "…" : formatEuro(total)}</b>
        </div>
      )}
      {table ? (
        <>
          {draft.length > 0 && (
            <button type="button" className="btn primary ph-big" disabled={busy} onClick={onSend}>
              <AppIcon name="kitchen" />
              {text.toKitchen(draft.length)}
            </button>
          )}
          {any && (
            <div className="ob-row">
              <button type="button" className="btn" disabled={busy} onClick={onBill}>
                <AppIcon name="bill" />
                {text.bill}
              </button>
              <button type="button" className="btn primary" disabled={busy} onClick={onPay}>
                <AppIcon name="pay" />
                {text.pay}
              </button>
            </div>
          )}
        </>
      ) : (
        draft.length > 0 && (
          <div className="ob-row">
            <button type="button" className="btn" disabled={busy || total === null} onClick={onLater}>
              {text.later}
            </button>
            <button type="button" className="btn primary" disabled={busy || total === null} onClick={onPay}>
              <AppIcon name="pay" />
              {text.payNow}
            </button>
          </div>
        )
      )}
    </>
  );
}
